/*!
 * @copyright	Copyright (C) 2024, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 */
/**
 * @version 1.0.0
 * @changelog
 *  2024-10-28 (1.0.0) (jhoward)
 *    - Add image-map-size-scale.ts to facilitate responsive image maps using the ResizeObserver API.
 */

import {ResizeObserver as ResizeObserverPolyfill, ResizeObserverEntry} from '@juggle/resize-observer';

const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

(function () {
  /**
   * Set the area coordinates for all image maps in the page content.
   */
  function setAreaCoordinates(resizeObserverEntries: ResizeObserverEntry[]) {
    resizeObserverEntries.forEach(entry => {
      const mapElement = getMapForImage(entry.target);
      if (mapElement && originalAreaCoordinates[mapElement.name]) {
        const scaleFactor = entry.contentRect.width / originalAreaCoordinates[mapElement.name].originalWidth;
        //loop through all HTMLAreaElements in the map and scale their coordinates
        mapElement.querySelectorAll('area').forEach((areaElement, areaIndex) => {
          const newAreaCoordinates = originalAreaCoordinates[mapElement.name].coords[areaIndex].map((coordinate) => {
            return coordinate * scaleFactor;
          });
          areaElement.coords = newAreaCoordinates.join(',');
        });
      }
    });
  }

  function getMapForImage(imageElement: Element): HTMLMapElement | null {
    const mapName = imageElement.getAttribute('usemap')?.replace('#', '') || '';
    return document.querySelector(`map[name="${mapName}"]`);
  }

  /**
   * Get the natural width of an image
   */
  function getImageNaturalWidth(imageElement: HTMLImageElement): number {
    const img = new Image();
    img.src = imageElement.src;
    return img.naturalWidth || DEFAULT_IMAGE_WIDTH;
  }

  const DEFAULT_IMAGE_WIDTH = 1000;
  const IMAGES: NodeListOf<HTMLImageElement> = document.querySelectorAll('.page-content img[usemap]');
  const IMAGE_MAPS = Array.from(IMAGES).map(getMapForImage).filter(map => map) as HTMLMapElement[];
  const originalAreaCoordinates: {[key: string]: {originalWidth: number, coords: number[][]}} = {};

  //Initialize the original area coordinates and original image widths for each image map
  IMAGE_MAPS.forEach((mapElement, mapIndex) => {
    originalAreaCoordinates[mapElement.name] = {
      originalWidth: getImageNaturalWidth(IMAGES[mapIndex]),
      coords: []
    };
    mapElement.querySelectorAll('area').forEach((areaElement) => {
      //save coordinates for each HTMLAreaElement as an array of integers
      originalAreaCoordinates[mapElement.name].coords.push(areaElement.coords.split(',').map((coordinate) => parseInt(coordinate)));
    });
  });

  const resizeObserver = new ResizeObserver(setAreaCoordinates);
  IMAGES.forEach(image => {
    resizeObserver.observe(image);
  });

  window.addEventListener('load', () => {
    //when images have loaded, update the natural/native image widths to make sure our scaling is accurate
    IMAGE_MAPS.forEach((mapElement, mapIndex) => {
      originalAreaCoordinates[mapElement.name].originalWidth = getImageNaturalWidth(IMAGES[mapIndex]);
    });
  });
})();